import { v4 as uuidv4 } from 'uuid';
import JSZip from 'jszip';
import { Storage } from 'aws-amplify';
const PREFIX = 'accounts/';

Storage.configure({
    bucket: 'xplanung24211812-main',
    level: 'public',
    region: 'eu-central-1',
});

export const S3UploadHandler = async (files, path) => {
    const batchUpload = files.map(async (file) => {
        const fileId = uuidv4();
        const filename = file.name;
        const key = `${PREFIX}${path}/${filename}`;

        try {
            await Storage.put(key, file);
        } catch (error) {
            console.error('Error uploading file: ', error);
            return null;
        }
    
        return fileId;
    });

    const fileIds = await Promise.all(batchUpload);
    return fileIds;
};

export const S3PlanFileDownloader = async (plan) => {
      try {
          const url = await S3GetFileUrl(`${PREFIX}${plan?.companiesid}/plans/${plan?.id}/master.gml`);
          const fileName = `${plan?.informations?.fileName ? plan.informations.fileName : plan?.planId ? plan.planId : "master"}.gml`;
          const response = await fetch(url);
          const blob = await response.blob();

          const link = document.createElement('a');
          const objectUrl = URL.createObjectURL(blob); // Create a URL for the blob
          link.href = objectUrl;
          link.download = fileName; // Set the custom file name
          link.click();

          // Clean up the object URL after download
          URL.revokeObjectURL(objectUrl);
      } catch (error) {
            console.error('Error generating download URL:', error);
      }
};

export const S3PlanUploadHandler = async ({files, path, isRef}) => {
    let gmlFileName;
    const batchUpload = files.map(async (file) => {
        const filename = file.name;

        if (filename.endsWith('.zip')) {
            try {
                const zip = new JSZip();
                const zipContent = await zip.loadAsync(file)

                const extractedFiles = [];
                for (const [relativePath, zipEntry] of Object.entries(zipContent.files)) {
                    if (!zipEntry.dir) {
                        const fileContent = await zipEntry.async('blob'); // Extract file as Blob
                        const extractedFilename = relativePath.split('/').pop(); // Flatten the file structure
                        const sanitizedFilename = extractedFilename;
                        const key = `${PREFIX}${path}/${sanitizedFilename}`;

                        // Upload the extracted file to S3
                        await Storage.put(key, fileContent);
                        extractedFiles.push({
                            name: sanitizedFilename,
                            size: fileContent.size,
                            type: sanitizedFilename.split('.').pop().toUpperCase(),
                            key: key,
                        });
                    }
                }
                return extractedFiles;
            } catch (error) {
                console.error('Error extracting ZIP file:', error);
                return null;
            }
        }

        let s3Filename = filename;

        // check if file is a gml file
        if (filename.endsWith('.gml') && !isRef) {
            gmlFileName = filename.replace('.gml', '');
            s3Filename = `master.gml`;
        }

        const key = `${PREFIX}${path}/${s3Filename}`;

        const fileData = {
            name: file.name,
            size: file.size,
            type: filename.split('.').pop().toUpperCase(),
            key: key,
        }

        try {
            await Storage.put(key, file);
        } catch (error) {
            console.error('Error uploading file: ', error);
            return null;
        }

        return fileData;
    });

    const uploadedFiles = await Promise.all(batchUpload);
    return {files: uploadedFiles.flat(), gmlFileName};
};

export const S3ReviewUploadHandler = async ({files, path}) => {
    const batchUpload = files.map(async (file) => {
        const filename = file.name;

        const key = `${PREFIX}${path}/${filename}`;

        try {
            await Storage.put(key, file);
        } catch (error) {
            console.error('Error uploading file: ', error);
            return null;
        }

        const fileData = {
            name: file.name,
            size: file.size,
            type: filename.split('.').pop().toUpperCase(),
            key: key,
        }
        return fileData;
    });

    const uploadedFiles = await Promise.all(batchUpload);
    return uploadedFiles;
};

export const S3CopyPlanToOrder = async (companyId, id, path) => {
    const filename = path.split('/').pop();
    const dest = `${PREFIX}${companyId}/orders/${id.replaceAll(
        '-',
        '/'
    )}/documents/${filename}`;

    const CopySource = `${PREFIX}${path}`;

    try {
        await Storage.copy({
                key: CopySource,
                level: 'public',
            }, {
                key: dest,
                level: 'public',
            },
        );
    } catch (error) {
        console.error('Error copying file: ', error);
        return null;
    }
    return dest;
};

export const S3ReadFile = async (path) => {
    try {
        const fileContent = await Storage.get(`${PREFIX}${path}`, { download: true });
        return await fileContent.Body.text()
    } catch (error) {
        console.error('Error reading file: ', error);
        return null;
    }
};

export const S3DeleteAllFilesInDirectory = async (path) => {
    const result = await Storage.list(`${PREFIX}${path}`);
    const files = result.results;

    if (!files || files?.length === 0) {
        return;
    }

    try {
        for (const file of files) {
            await Storage.remove(file.key);
        }
        return true;
    } catch (error) {
        console.error('Error deleting files: ', error);
    }
    return false;
};

export const S3CopyPlanFromOrder = async (companyId, path) => {
    try {
        const files = await Storage.list(`${PREFIX}${path}`);

        if (files.results.length === 0) {
            console.error('No files found in path: ', path);
            return;
        }

        const batchCopy = files.results.filter((file) => {
            if (!file.key.endsWith('.gml')) {
                return false;
            }
            return true;
        }).map(async (file) => {
            const planId = uuidv4();
            const gmlFileName = file.key.split('/').pop().replace('.gml', '');
            const dest = `${PREFIX}${companyId}/plans/${planId}/master.gml`;


            const CopySource = `${file.key}`;

            try {
                await Storage.copy({
                        key: CopySource,
                        level: 'public',
                    },{
                        key: dest,
                        level: 'public',
                    },
                );
            } catch (error) {
                console.error('Error copying file: ', error);
                return null;
            }
            return {planId, gmlFileName, companyId};
        });

        const fileIds = await Promise.all(batchCopy);
        return fileIds;
    } catch (error) {
        console.error('Error copying file: ', error);
    }
};

export const S3ListFiles = async (path) => {

    const files = await Storage.list(`${PREFIX}${path}`);
    return files.results;
};

export const S3DeleteFile = async (key) => {
    await Storage.remove(key);
    return true;
};

export const S3GetFileUrl = async (key) => {
    const url = await Storage.get(key);
    return url;
};